// ─────────────────────────────────────────────────────────────────────────────
// V2LegalLayout — v2 shell for /privacy and /terms. Same props as components/LandingPage/
// LegalLayout (which switches to this in v2); the legal text and its building blocks
// (Section, P, UL, Callout, ContactCard → .lg-* markup) are unchanged and restyled under .v2lg.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./v2.css";
import "./v2-pages.css";
import { useAppearance } from "./appearance";
import { Glyph } from "./icons";

const reduced = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export default function V2LegalLayout({ current, eyebrow, title, lede, meta = [], sections = [], children }) {
  useAppearance();
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState(sections[0]?.id);
  useEffect(() => {
    const fn = () => {
      setScrolled(window.scrollY > 6);
      const passed = sections.map((s) => { const el = document.getElementById(s.id); return el ? { id: s.id, top: el.getBoundingClientRect().top } : null; }).filter((o) => o && o.top <= 130);
      setActive(passed.length ? passed[passed.length - 1].id : sections[0]?.id);
    };
    fn(); window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, [sections]);
  const jump = (e, id) => {
    const el = document.getElementById(id); if (!el) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: reduced() ? "auto" : "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
    const d = e.currentTarget.closest("details"); if (d) d.open = false;
  };
  return (
    <div className="v2 v2lg">
      <div className="v2-backdrop" aria-hidden="true" />
      <nav className={`v2lg-nav${scrolled ? " is-scrolled" : ""}`} aria-label="Main">
        <Link to="/" className="v2ob-brand v2au-brand"><span className="v2ob-brand-mark">e</span>EazyBilling</Link>
        <div className="v2lg-nav-links">
          <Link to="/privacy" className={current === "privacy" ? "is-on" : ""} aria-current={current === "privacy" ? "page" : undefined}>Privacy</Link>
          <Link to="/terms" className={current === "terms" ? "is-on" : ""} aria-current={current === "terms" ? "page" : undefined}>Terms</Link>
          <Link to="/login" className="v2-btn v2-btn--sm">Sign in</Link>
        </div>
      </nav>
      <header className="v2lg-head">
        <span className="v2k-eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        {lede && <p>{lede}</p>}
        {meta.length > 0 && <dl className="v2lg-meta">{meta.map(({ label, value }) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>}
      </header>
      <div className="v2lg-body">
        <aside className="v2lg-toc" aria-label="Contents">
          <span className="v2lg-toc-label">On this page</span>
          <ol>{sections.map((s) => <li key={s.id}><a href={`#${s.id}`} className={active === s.id ? "is-on" : ""} aria-current={active === s.id ? "true" : undefined} onClick={(e) => jump(e, s.id)}>{s.title}</a></li>)}</ol>
        </aside>
        <main className="v2lg-article">
          <details className="v2lg-toc-mobile">
            <summary>On this page <Glyph name="chevronDown" size={14} stroke={2} /></summary>
            <ol>{sections.map((s) => <li key={s.id}><a href={`#${s.id}`} onClick={(e) => jump(e, s.id)}>{s.title}</a></li>)}</ol>
          </details>
          {children}
        </main>
      </div>
      <footer className="v2lg-foot">
        <span>© {new Date().getFullYear()} EazyBilling</span>
        <span><Link to="/">Home</Link><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link></span>
      </footer>
    </div>
  );
}
